import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v6";
import { lazy, memo, type ReactNode, Suspense } from "react";
import { Navigate, Outlet } from "react-router-dom";

import { AuthDataProvider } from "./auth/auth-data-context";
import { AuthGuard } from "./auth/auth-guard";
import { AuthTokenProvider, useAuthToken } from "./auth/auth-token-context";
import { ImpersonatePage } from "./auth/impersonate";
import { LoginPage } from "./auth/login";
import { VerifyPage } from "./auth/verify";
import { RedoPartnersRpcClientProvider } from "./rpc/client-context";
import { AppShell } from "./shell/app-shell";

// Authenticated routes are lazy so their bundle (and the dashboard's
// chart + tables + checklist) doesn't ship with the login/verify
// entry. VerifyPage's success path kicks off `import("./dashboard")`
// before navigating so the chunk is already cached by the time the
// route resolves.
const Dashboard = lazy(() => import("./dashboard"));
const EarningsPage = lazy(() =>
  import("./earnings").then((m) => ({ default: m.EarningsPage })),
);
const ProductPage = lazy(() =>
  import("./products/product-page").then((m) => ({ default: m.ProductPage })),
);
const SettingsPage = lazy(() =>
  import("./settings").then((m) => ({ default: m.SettingsPage })),
);

// Composes the providers that need to live inside the Router (so they
// can call useLocation, useNavigate, etc.) and renders the matched
// route via Outlet.
const RootLayout = memo(function RootLayout() {
  return (
    <AuthTokenProvider>
      <RpcClientWithToken>
        <AuthDataProvider>
          <Outlet />
        </AuthDataProvider>
      </RpcClientWithToken>
    </AuthTokenProvider>
  );
});

// Bridges the AuthTokenContext into the RPC client provider — the
// client must be reconstructed whenever the token changes so the
// Authorization header reflects the latest auth state.
const RpcClientWithToken = memo(function RpcClientWithToken({
  children,
}: {
  children: ReactNode;
}) {
  const { token } = useAuthToken();
  return (
    <RedoPartnersRpcClientProvider authToken={token}>
      {children}
    </RedoPartnersRpcClientProvider>
  );
});

const lazyPage = (Page: React.ComponentType): React.ReactElement => (
  <Suspense fallback={null}>
    <Page />
  </Suspense>
);

// Explicit annotation: the inferred return type of createBrowserRouter
// references `@remix-run/router` (an internal of react-router-dom that
// the rules_javascript runfiles paths can't represent portably).
// `ReturnType<...>` keeps the annotation self-contained.
export const router: ReturnType<typeof createBrowserRouter> =
  createBrowserRouter([
    {
      element: <RootLayout />,
      children: [
        { index: true, element: <Navigate replace to="/dashboard" /> },
        { path: "/login", element: <LoginPage /> },
        { path: "/auth/verify", element: <VerifyPage /> },
        { path: "/impersonate", element: <ImpersonatePage /> },
        {
          // Auth gate first, then shell. AppShell renders the
          // sidebar + per-page <PageErrorBoundary> wrapping <Outlet/>.
          element: <AuthGuard />,
          children: [
            {
              element: <AppShell />,
              children: [
                { path: "/dashboard", element: lazyPage(Dashboard) },
                { path: "/earnings", element: lazyPage(EarningsPage) },
                {
                  path: "/products/:productId",
                  element: lazyPage(ProductPage),
                },
                { path: "/settings", element: lazyPage(SettingsPage) },
              ],
            },
          ],
        },
        { path: "*", element: <Navigate replace to="/" /> },
      ],
    },
  ]);
